"use client";

import { useMemo } from "react";
import { analyzeArticle, enhanceHtmlForPreview, EMPTY_REPORT, type AnalyzeMeta, type ScoreReport } from "@/lib/articleAnalysis";
// Co-locating the CSS with the component means any route that mounts
// ArticlePreview (in-app editor preview AND the public /blog/[slug]
// detail page) automatically gets the styles. Without this, the public
// blog detail page rendered unstyled body copy because article-page.css
// only loads on /articles/[id].
import "./article-preview.css";

type ArticlePreviewProps = {
    html: string;
    title: string;
    keyword: string | null;
    metaDescription: string | null;
    featuredImageUrl: string | null;
    publishedAt: string | null;
    contentTypeLabel: string | null;
    siteHost: string | null;
    // Optional: pass a pre-computed report so the sibling score panel and the
    // preview share one analysis pass. Components above us call analyzeArticle
    // once per HTML change.
    report?: ScoreReport;
};

function formatDate(iso: string | null): string | null {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return null;
    }
}

export default function ArticlePreview({
    html,
    title,
    keyword,
    metaDescription,
    featuredImageUrl,
    publishedAt,
    contentTypeLabel,
    siteHost,
    report,
}: ArticlePreviewProps) {
    const analysisMeta: AnalyzeMeta = useMemo(
        () => ({ title, keyword, metaDescription, featuredImageUrl, siteHost }),
        [title, keyword, metaDescription, featuredImageUrl, siteHost],
    );

    const { toc, readingMinutes } = useMemo(() => {
        if (report && report !== EMPTY_REPORT) {
            return { toc: report.toc, readingMinutes: report.stats.readingMinutes };
        }
        const r = analyzeArticle(html, analysisMeta);
        return { toc: r.toc, readingMinutes: r.stats.readingMinutes };
    }, [report, html, analysisMeta]);

    // Enhancement pipeline — pure string → string, memoised on html change.
    const enhancedHtml = useMemo(() => enhanceHtmlForPreview(html), [html]);

    const prettyDate = formatDate(publishedAt);

    return (
        <article className="article-preview">
            <header className="article-preview-hero">
                {featuredImageUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        className="article-preview-hero-image"
                        src={featuredImageUrl}
                        alt={title}
                    />
                )}
                {contentTypeLabel && (
                    <div className="article-preview-kicker">{contentTypeLabel}</div>
                )}
                <h1 className="article-preview-title">{title || "Untitled article"}</h1>
                <div className="article-preview-meta">
                    {keyword && (
                        <>
                            <span>{keyword}</span>
                            <span className="dot" />
                        </>
                    )}
                    <span>{readingMinutes} min read</span>
                    {prettyDate && (
                        <>
                            <span className="dot" />
                            <span>{prettyDate}</span>
                        </>
                    )}
                </div>
            </header>

            {toc.length >= 3 && (
                <nav className="article-preview-toc" aria-label="Table of contents">
                    <div className="article-preview-toc-title">Table of Contents</div>
                    <ol className="article-preview-toc-list">
                        {toc.map((h) => (
                            <li key={h.slug} data-level={h.level}>
                                <a href={`#${h.slug}`}>{h.text}</a>
                            </li>
                        ))}
                    </ol>
                </nav>
            )}

            <div
                className="article-preview-content"
                dangerouslySetInnerHTML={{ __html: enhancedHtml }}
            />
        </article>
    );
}
